import React, { useEffect, useState, useCallback, useMemo } from "react";
import Slider from "react-slick";
import { useDispatch, useSelector } from "react-redux";

import { HeaderTabs, RecentTranactions } from "../../components";
import PrimaryCardBG from "../../components/Card/PrimaryCardBG";
import OtherCards from "../../components/OthersCards";
import ViewRecentTransaction from "../../components/RecentTranactions/partials/ViewRecentTransaction";
import EmptyCard from "../../components/Empty/EmptyCard";
import {
  CardButtonSkeleton,
  OtherCardsSkeletons,
  PrimaryActiveCardSkeleton,
  RecentTransactionsSkeleton,
} from "../../components/Skeleton";
import CustomTransition from "../../components/CustomTransition";
import { setPageTitle } from "../../helper/metaDataHelper";
import CardButton from "../../components/Card/CardButton";
import AddNewCardModal from "../../components/Modals/Card/AddNewCardModal";
import ReccentTransactionSideBar from "../../components/Common/RecentTransactionsSideBar";

import { GetCards } from "../../redux/actions/Cards/GetCards";
import { getModuleStatus } from "../../redux/actions/lookups/getModuleStatus";
import { setActiveCard } from "../../redux/actions/Cards/setActiveCard";
import { useRecentTransaction } from "../../hooks/useRecentTransaction";

// ⚙️ Extract slider settings outside component
const sliderSettings = {
  dots: true,
  infinite: false,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
};

const Card = () => {
  const dispatch = useDispatch();

  const [activeTransaction, setActiveTransaction] = useState({});
  const [showRecentTransaction, setShowRecentTransaction] = useState(false);
  const [recentTransactionsLoading, setRecentTransactionsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ Combine related selectors
  const { hasCard, activeCard, clientCards } = useSelector((state) => ({
    hasCard: state.client.clientInfo.has_card,
    activeCard: state.activeCard.activeCard,
    clientCards: state.clientCards.clientCards,
  }));

  const { lastThreeTransactions } = useRecentTransaction(setRecentTransactionsLoading, "card");

  useEffect(() => {
    setPageTitle("Cards");
    dispatch(getModuleStatus({ module: "MatchMove" }));
  }, [dispatch]);

  // 🔹 Fetch cards if user has cards
  useEffect(() => {
    if (!hasCard) return;

    const fetchCards = async () => {
      try {
        setLoading(true);
        // ✅ Better error handling
        const res = await dispatch(GetCards());
      if (!res?.success) {
        setError("Failed to fetch cards. Please try again later.");
      }
      } catch {
        setError("Unable to load cards. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchCards();
  }, [dispatch, hasCard]);

  // 🔹 Handle card change in slider
  const handleCardChange = useCallback((index) => {
      const selectedCard = clientCards[index];
      if (selectedCard) dispatch(setActiveCard(selectedCard));
    },
    [clientCards, dispatch]
  );

  // 🔹 Memoize slider settings with callback
  const sliderConfig = useMemo(() => ({ ...sliderSettings, afterChange: handleCardChange }), [handleCardChange]);

  // 🔹 Simplify conditional render logic
  const renderMainCardSection = () => {
    if (!hasCard) return <EmptyCard />;
    if (loading)
      return (
        <>
          <PrimaryActiveCardSkeleton />
          <RecentTransactionsSkeleton />
        </>
      );
    if (error) return <EmptyCard />;
    if (!activeCard) return null;

    return (
      <div className="space-y-4">
        {/* Mobile slider */}
        <div className="block md:hidden pb-3">
          <Slider {...sliderConfig}>
            {clientCards.map((card) => (
              <PrimaryCardBG key={card.id} type="card" activeCard={card} />
            ))}
          </Slider>
        </div>

        {/* Desktop active card */}
        <div className="hidden md:block">
          <PrimaryCardBG type="card" activeCard={activeCard} />
        </div>

        {/* Recent Transactions */}
        {!recentTransactionsLoading && lastThreeTransactions ? (
          <div className="max-[360px]:px-2">
            <RecentTranactions
              activeTransaction={activeTransaction}
              transactions={lastThreeTransactions}
              setActiveTransaction={setActiveTransaction}
              setShowRecentTransaction={setShowRecentTransaction}
              activeCard={activeCard}
              type="card"
            />
          </div>
        ) : (
          <RecentTransactionsSkeleton />
        )}

        {/* Add new card button (mobile only) */}
        <div className="md:hidden block max-[360px]:px-1.5">
          <AddNewCardModal>
            <CardButton
              title="Add New Card"
              subtitle="Let's get your card ready!"
            />
          </AddNewCardModal>
        </div>
      </div>
    );
  };

  const renderSidebarSection = () => {
    if (!hasCard) return null;

    if (loading)
      return (
        <div className="xl:w-[404px] w-[350px] hidden md:block">
          <OtherCardsSkeletons />
          <CardButtonSkeleton />
        </div>
      );

    if (clientCards?.length > 0)
      return (
        <div className="xl:w-[404px] w-[350px] hidden md:block mt-2">
          <CustomTransition show={showRecentTransaction}>
            {showRecentTransaction && (
              <div className="view-recent-transaction-wrapper h-[700px]">
                <ViewRecentTransaction
                  transaction={activeTransaction}
                  setShowRecentTransaction={setShowRecentTransaction}
                  ShowRecentTransaction={showRecentTransaction}
                  setActiveTransaction={setActiveTransaction}
                  type="card"
                />
              </div>
            )}
          </CustomTransition>

          <CustomTransition show={!showRecentTransaction}>
            {!showRecentTransaction && (
              <OtherCards cards={clientCards} activeCard={activeCard} />
            )}
          </CustomTransition>
        </div>
      );

    return null;
  };

  return (
    <main>
      <div className="max-w-[1024px] mx-auto md:px-4 px-1 max-[360px]:px-1 min-h-screen">
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          {/* Left section */}
          <div className="md:w-[572px] w-full sm:flex-grow mb-6 sm:mb-0 space-y-3">
            <HeaderTabs />
            {renderMainCardSection()}
          </div>

          {/* Right section */}
          {renderSidebarSection()}

          {/* Mobile sidebar */}
          <ReccentTransactionSideBar
            ShowRecentTransaction={showRecentTransaction}
            setShowRecentTransaction={setShowRecentTransaction}
            activeTransaction={activeTransaction}
            setActiveTransaction={setActiveTransaction}
            type="card"
          />
        </div>
      </div>
    </main>
  );
};

export default Card;
